<template>
  <header class="AppHeader AppHeader--sticky">
    <div class="AppHeader-container PageContainer">
       <!-- TODO CVO-19899 [Redirect] Remove "external" once booking flow in released in Veloair -->
      <NuxtLink class="AppHeader-logo" :to="homePath" external>
        <img
          :src="logoSrc"
          :alt="t('custom.meta.title')"
          class="AppHeader-logoImg"
        >
      </NuxtLink>

      <div v-if="!hideActions" class="AppHeader-actions">
        <!-- Teleport target for home page CTA button -->
        <div id="header-cta-slot" />

        <template v-if="isTabletOrDesktop">
          <LangCurrencyToggler data-test-id="language-currency-menu" />
          <DesktopMenu data-test-id="user-menu"/>
        </template>
        <MobileMenu v-else data-test-id="user-menu" />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import DesktopMenu from '~/components/domain/Account/User/DesktopMenu.vue';
import LangCurrencyToggler from '~/components/domain/LangCurrencyToggler/LangCurrencyToggler.vue';
import MobileMenu from '~/components/domain/Account/User/MobileMenu.vue';

interface Props {
  hideActions?: boolean;
}

withDefaults(defineProps<Props>(), {
  hideActions: false,
});

const { t } = useI18n();
const { isTabletOrDesktop } = useResponsive();
const { homePath } = useHome();
const staticAsset = useStaticAsset();

const logoSrc = computed(() => {
  return isTabletOrDesktop.value ? staticAsset.get('logo.large') : staticAsset.get('logo.small');
});
</script>

<style lang="scss">
@use 'assets/scss/theme' as vars;
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-base {
  .AppHeader {
    // Special style for PanelDropdown in the header to avoid unreadable label
    .PanelDropdown-trigger {
      border-radius: vars.$organisms-panel-dropdown-border-radius;
      background-color: vars.$organisms-panel-dropdown-button-default-bg-color;
      transition: background-color vars.$transition-fast ease;
      border-radius: vars.$dimensions-d-30;

      &[data-state=open] {
        background-color: vars.$color-neutral-gray-50;
      }
    }
  }

  .AppHeader {
    background-color: vars.$organisms-header-bg-color;
    border-bottom: vars.$organisms-header-border-stroke solid vars.$organisms-header-border-color;
    border-radius: vars.$organisms-header-sm-border-radius;
    width: 100%;
    z-index: vars.$z-index-header;

    color: vars.$atoms-actionbutton-invert-enabled-text-color;
    --icon-button-hover-background: transparent;

    &--sticky {
      position: sticky;
      top: 0;
    }

    &-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: vars.$header-min-height-mobile;

      @include responsive.tablet {
        height: vars.$header-min-height-desktop;
      }

      &.PageContainer {
        @include responsive.mobile {
          padding-block: vars.$organisms-header-sm-padding-top-bottom;
        }

        @include responsive.tablet {
          padding-block: vars.$organisms-header-lg-md-padding-top-bottom;
        }
      }
    }

    &-logo {
      display: flex;
      align-items: center;
    }

    &-logoImg {
      height: 32px;
      width: auto;

      @include responsive.tablet {
        height: 40px;
      }
    }

    &-actions {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-3;
    }

    &-dropdownBtn {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
      padding: vars.$dimensions-d-3;
      background-color: transparent;
      border: none;
      border-radius: vars.$border-radius-full;
      font-size: vars.$font-size-md;
      font-weight: vars.$typography-weight-strong;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }

      &:focus {
        outline: 2px solid rgba(255, 255, 255, 0.3);
        outline-offset: 2px;
      }
    }

    &-dropdownText {
      font-family: 'Work Sans', sans-serif;
      font-weight: vars.$typography-weight-strong;
      white-space: nowrap;
    }

    &-dropdownIcon {
      flex-shrink: 0;
    }
  }

  // Responsive design
  @include responsive.tablet {
    .AppHeader {
      border-radius: vars.$organisms-header-lg-md-border-radius;

      &-logoImg {
        height: 36px;
      }

      &-dropdownBtn {
        padding: vars.$dimensions-d-2 vars.$dimensions-d-3;
        font-size: vars.$font-size-sm;
      }
    }
  }

  @include responsive.mobile {
    .AppHeader {
      &-actions {
        gap: vars.$dimensions-d-2;
      }

      &-dropdownText {
        font-size: vars.$font-size-sm;
      }
    }
  }
}
</style>
